<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

import bridgeProxyAPI from 'dashboard/api/bridgeProxy';

export default {
  components: {
    PageHeader,
    NextButton,
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      bridgeUrl: 'http://localhost:3200',
      accountName: '',
      showQRModal: false,
      qrImage: null,
      qrStatus: 'idle',
      qrMessage: '',
      eventSource: null,
      createdInboxId: null,
      connectedUser: null,
      pollingTimeout: null,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'inboxes/getUIFlags',
    }),
  },
  validations: {
    bridgeUrl: { required },
    accountName: { required },
  },
  beforeUnmount() {
    this.closeQREventSource();
    this.clearPolling();
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }

      try {
        const waChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.accountName || 'WhatsApp',
            channel: {
              type: 'whatsapp_unofficial',
              bridge_url: this.bridgeUrl,
              account_name: this.accountName,
            },
          }
        );

        this.createdInboxId = waChannel.id;

        // Show QR modal for login
        this.showQRModal = true;
        this.startQRLogin(waChannel.id);
      } catch (error) {
        useAlert(
          error.message || 'Failed to create WhatsApp channel. Please try again.'
        );
      }
    },

    async startQRLogin(inboxId) {
      this.qrStatus = 'waiting';
      this.qrMessage = 'Connecting to WhatsApp Bridge...';

      try {
        // Tell bridge (via rails proxy) to start QR login
        await bridgeProxyAPI.reconnect(inboxId);

        // Start polling for QR and connection status
        this.pollQRStatus(inboxId);
      } catch (error) {
        this.qrStatus = 'error';
        this.qrMessage = error.message || 'Cannot connect to WhatsApp Bridge';
      }
    },

    clearPolling() {
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout);
        this.pollingTimeout = null;
      }
    },

    async pollQRStatus(inboxId) {
      this.clearPolling();

      const poll = async () => {
        if (!this.showQRModal) return;

        try {
          const response = await bridgeProxyAPI.getQrData(inboxId);
          const data = response.data;

          if (data.status === 'connected') {
            this.qrStatus = 'connected';
            this.qrMessage = 'WhatsApp login successful!';
            this.connectedUser = data.session?.userInfo || data.userInfo || null;
            this.clearPolling();
            setTimeout(() => {
              this.showQRModal = false;
              this.navigateToAgents();
            }, 2000);
          } else if (data.status === 'qr_ready') {
            this.qrImage = data.qr;
            this.qrStatus = 'waiting';
            this.qrMessage = 'Open WhatsApp → Linked Devices → Link a Device → Scan this QR code';
          } else if (data.status === 'waiting') {
            this.qrStatus = 'waiting';
            this.qrMessage = 'Generating QR code...';
          } else if (data.status === 'error') {
            this.qrStatus = 'error';
            this.qrMessage = 'Error: ' + (data.message || 'Login failed');
            this.clearPolling();
          }
        } catch (error) {
          console.warn('Polling error:', error);
        }

        if (this.showQRModal && this.qrStatus !== 'connected' && this.qrStatus !== 'error') {
          this.pollingTimeout = setTimeout(poll, 3000);
        }
      };

      poll();
    },

    closeQREventSource() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
    },

    closeQRModal() {
      this.showQRModal = false;
      this.closeQREventSource();
      this.clearPolling();
      if (this.createdInboxId) {
        this.navigateToAgents();
      }
    },

    navigateToAgents() {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: {
          page: 'new',
          inbox_id: this.createdInboxId,
        },
      });
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      header-title="WhatsApp Personal Channel"
      header-content="Connect your personal WhatsApp account to send and receive messages directly from Chatwoot. Requires WhatsApp Bridge to be running."
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <div class="flex-shrink-0 flex-grow-0">
        <label :class="{ error: v$.accountName.$error }">
          WhatsApp Account Name
          <input
            v-model="accountName"
            type="text"
            placeholder="e.g. Sales Team"
            @blur="v$.accountName.$touch"
          />
        </label>
        <p class="help-text">
          Give a name to distinguish between multiple WhatsApp accounts
        </p>
      </div>

      <div class="flex-shrink-0 flex-grow-0 mt-4">
        <label :class="{ error: v$.bridgeUrl.$error }">
          WhatsApp Bridge URL
          <input
            v-model="bridgeUrl"
            type="text"
            placeholder="http://localhost:3200"
            @blur="v$.bridgeUrl.$touch"
          />
        </label>
        <p class="help-text">
          URL of the WhatsApp Bridge service. Default:
          <code>http://localhost:3200</code>
        </p>
      </div>

      <div class="w-full mt-4">
        <NextButton
          :is-loading="uiFlags.isCreating"
          type="submit"
          solid
          blue
          label="Create Channel & Scan QR"
        />
      </div>
    </form>

    <!-- QR Modal -->
    <teleport to="body">
      <div
        v-if="showQRModal"
        class="fixed inset-0 z-50 flex items-center justify-center"
        style="background: rgba(0, 0, 0, 0.6); backdrop-filter: blur(4px)"
      >
        <div
          class="bg-white dark:bg-slate-800 rounded-2xl p-8 max-w-sm w-full mx-4 shadow-2xl"
        >
          <h2
            class="text-lg font-semibold text-center mb-4 text-slate-800 dark:text-slate-100"
          >
            Scan QR Code with WhatsApp
          </h2>

          <div class="flex justify-center mb-4">
            <div
              v-if="qrImage"
              class="rounded-xl overflow-hidden bg-white p-2"
            >
              <img
                :src="qrImage"
                alt="WhatsApp QR Code"
                class="w-64 h-64 object-contain"
              />
            </div>
            <div
              v-else
              class="w-64 h-64 bg-slate-100 dark:bg-slate-700 rounded-xl flex items-center justify-center"
            >
              <div class="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full" />
            </div>
          </div>

          <!-- Connected user info -->
          <div
            v-if="qrStatus === 'connected' && connectedUser"
            class="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
          >
            <div class="flex items-center gap-3">
              <div>
                <p class="font-medium text-green-800 dark:text-green-300">
                  {{ connectedUser.displayName || 'Connected' }}
                </p>
                <p v-if="connectedUser.phoneNumber" class="text-xs text-green-600 dark:text-green-400">
                  {{ connectedUser.phoneNumber }}
                </p>
              </div>
            </div>
          </div>

          <p
            class="text-center text-sm mb-4"
            :class="{
              'text-slate-500 dark:text-slate-400': qrStatus === 'waiting',
              'text-green-600 dark:text-green-400 font-medium': qrStatus === 'connected',
              'text-red-500 dark:text-red-400': qrStatus === 'error',
            }"
          >
            {{ qrMessage }}
          </p>

          <div class="flex justify-center gap-3">
            <button
              class="px-4 py-2 text-sm font-medium rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
              @click="closeQRModal"
            >
              {{ qrStatus === 'connected' ? 'Continue' : 'Close' }}
            </button>
            <button
              v-if="qrStatus === 'error'"
              class="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors"
              @click="startQRLogin(createdInboxId)"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
